// System › Links service. Tables are newer than the generated types, so the
// client is loosened here only.
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { keepVariant, readLink, stickyVariant, type LinkOffer } from "./attribution";
import { addLinkParams } from "../../../supabase/functions/_shared/link-tracking";

const db = supabase as any;

export interface LinkRecord {
  id: string; slug: string; title: string; destination: string | null;
  redirect_type: number; utm: Record<string, string>; tags: string[];
  owner_user_id: string | null; coupon_id: string | null;
  landing: "destination" | "signup" | "pricing"; attribution_days: number;
  commission_enabled: boolean; expires_at: string | null; is_active: boolean;
  created_at: string; updated_at: string;
}
export type LinkDraft = Omit<LinkRecord, "id" | "created_at" | "updated_at"> & { id?: string };

export interface CouponRecord {
  id: string; code: string; name: string; discount_type: "percent" | "amount";
  percent_off: number | null; amount_off_cents: number | null; currency: string;
  plan_keys: string[]; intervals: string[];
  duration: "once" | "repeating" | "forever" | "until"; duration_months: number | null;
  discount_ends_at: string | null; expires_at: string | null; max_redemptions: number | null;
  audiences: string[]; skip_checkout: boolean; allow_stacking: boolean; stripe_coupon_id: string | null; is_active: boolean;
  created_at: string;
}
export type CouponDraft = Omit<CouponRecord, "id" | "created_at" | "stripe_coupon_id"> & { id?: string };

export interface PublicLinkOffer extends LinkOffer {
  title: string; landing: string; live: boolean; has_coupon: boolean; attribution_days: number;
  duration: string; duration_months: number | null;
}

export const LINK_HOSTS = ["links.podgo.io"];
export const isLinksHost = () => typeof window !== "undefined" && LINK_HOSTS.includes(window.location.hostname);
export const publicLinkUrl = (slug: string) => `https://links.podgo.io/${slug}`;

export async function resolveLink(slug: string, qr: boolean, creative?: string | null) {
  const { data, error } = await db.functions.invoke("link-redirect", {
    body: { slug, qr, c: creative ?? undefined, v: stickyVariant(slug) ?? undefined, referrer: document.referrer || null },
  });
  if (error) return null;
  const res = data as { destination: string; live: boolean; landing: string; creative: string | null; variant?: string | null } | null;
  keepVariant(slug, res?.variant);
  return res;
}

/**
 * Where a resolved short link sends the browser. Internal pages get ?link= so
 * the main site can remember the visit (links.podgo.io has its own storage),
 * and stay on podgo.io even when scanned on links.podgo.io.
 */
export function linkLandingUrl(destination: string, slug: string, creative?: string | null, variant?: string | null): string {
  const dest = addLinkParams(destination, slug, creative, variant);
  return dest.startsWith("/") && window.location.hostname.startsWith("links.") ? `https://podgo.io${dest}` : dest;
}

export async function reportNotFound(path: string) {
  const { data } = await db.functions.invoke("log-not-found", {
    body: { path, host: window.location.hostname, referrer: document.referrer || null },
  });
  return (data ?? {}) as { destination?: string };
}

export async function reportPageView(path: string) {
  await db.functions.invoke("log-not-found", { body: { mode: "view", path } });
}

export async function fetchLinkOffer(slug: string): Promise<PublicLinkOffer | null> {
  const { data, error } = await db.rpc("get_link_offer", { _slug: slug });
  if (error) return null;
  return ((data ?? [])[0] as PublicLinkOffer) ?? null;
}

/**
 * The discount this visitor would get: the server-checked offer when signed
 * in, otherwise the live offer of the link they arrived through (display only).
 */
export function useLinkOffer(): LinkOffer | null {
  const { user } = useAuth();
  const slug = readLink();
  const { data } = useQuery({
    queryKey: ["link-offer", user?.id ?? "anon", user ? null : slug],
    enabled: !!user || !!slug,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<LinkOffer | null> => {
      if (user) {
        const { data } = await db.rpc("get_my_link_offer");
        return ((data ?? [])[0] as LinkOffer) ?? null;
      }
      const offer = await fetchLinkOffer(slug!);
      return offer?.live && offer.has_coupon ? offer : null;
    },
  });
  return data ?? null;
}

// ---- admin ----
export async function listLinks(): Promise<LinkRecord[]> {
  const { data, error } = await db.from("links").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}
export async function listLinksOverview() {
  const { data } = await db.rpc("get_links_overview");
  return new Map(((data ?? []) as { link_id: string; clicks: number; qr_scans: number; signups: number; paid: number; revenue_cents: number }[])
    .map((r) => [r.link_id, r]));
}
export async function saveLink(draft: LinkDraft) {
  const { id, ...fields } = draft;
  const row = { ...fields, slug: fields.slug.toLowerCase() };
  if (id) {
    const { error } = await db.from("links").update(row).eq("id", id);
    if (error) throw error;
    return;
  }
  const { data: auth } = await supabase.auth.getUser();
  const { error } = await db.from("links").insert({ ...row, created_by: auth.user?.id ?? null });
  if (error) throw error;
}
export async function deleteLink(id: string) {
  const { error } = await db.from("links").delete().eq("id", id);
  if (error) throw error;
}
export async function fetchLinkStats(linkId: string, from: Date, to: Date, includeBots: boolean) {
  const { data, error } = await db.rpc("get_link_stats", {
    _link_id: linkId, _from: from.toISOString(), _to: to.toISOString(), _include_bots: includeBots,
  });
  if (error) throw error;
  return data as LinkStats;
}
export interface LinkStats {
  clicks: number; unique_clicks: number; qr_scans: number; bot_clicks: number;
  signups: number; paid: number; revenue_cents: number; discount_cents: number;
  daily: { day: string; clicks: number; qr: number }[];
  devices: { label: string; n: number }[]; browsers: { label: string; n: number }[];
  os: { label: string; n: number }[]; countries: { label: string; n: number }[];
  referrers: { label: string; n: number }[];
}

export interface MissRecord { id: string; host: string; path: string; hits: number; bot_hits: number; last_referrer: string | null; status: string; first_seen: string; last_seen: string }
export async function listMisses(): Promise<MissRecord[]> {
  const { data, error } = await db.from("not_found_hits").select("*").order("hits", { ascending: false }).limit(500);
  if (error) throw error;
  return data ?? [];
}
export async function setMissStatus(id: string, status: "open" | "redirected" | "ignored" | "fixed") {
  const { error } = await db.from("not_found_hits").update({ status }).eq("id", id);
  if (error) throw error;
}

export interface RedirectRule { id: string; source_path: string; destination: string; redirect_type: number; hits: number; is_active: boolean }
export async function listRedirects(): Promise<RedirectRule[]> {
  const { data, error } = await db.from("redirect_rules").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}
export async function saveRedirect(r: Partial<RedirectRule> & { source_path: string; destination: string }) {
  const { id, hits: _h, ...fields } = r;
  if (id) {
    const { error } = await db.from("redirect_rules").update(fields).eq("id", id);
    if (error) throw error;
    return;
  }
  const { data: auth } = await supabase.auth.getUser();
  const { error } = await db.from("redirect_rules").upsert({ ...fields, created_by: auth.user?.id ?? null }, { onConflict: "source_path" });
  if (error) throw error;
}
export async function deleteRedirect(id: string) {
  const { error } = await db.from("redirect_rules").delete().eq("id", id);
  if (error) throw error;
}

export async function listCoupons(): Promise<CouponRecord[]> {
  const { data, error } = await db.from("coupons").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}
export async function couponRedemptionCounts() {
  const { data } = await db.from("coupon_redemptions").select("coupon_id");
  const m = new Map<string, number>();
  for (const r of (data ?? []) as { coupon_id: string }[]) m.set(r.coupon_id, (m.get(r.coupon_id) ?? 0) + 1);
  return m;
}
/** Saves the row, then syncs the Stripe coupon (the server re-creates it if terms changed). */
export async function saveCoupon(draft: CouponDraft) {
  const { id, ...fields } = draft;
  const row = { ...fields, code: fields.code.toUpperCase() };
  let couponId = id;
  if (id) {
    const { error } = await db.from("coupons").update(row).eq("id", id);
    if (error) throw error;
  } else {
    const { data: auth } = await supabase.auth.getUser();
    const { data, error } = await db.from("coupons").insert({ ...row, created_by: auth.user?.id ?? null }).select("id").single();
    if (error) throw error;
    couponId = data.id;
  }
  const { data, error } = await db.functions.invoke("stripe-sync-coupon", { body: { coupon_id: couponId } });
  if (error || data?.error) throw new Error(data?.error || "Saved, but syncing to Stripe failed. Try saving again.");
}
export async function deleteCoupon(id: string) {
  const { error } = await db.from("coupons").delete().eq("id", id);
  if (error) throw error;
}

export interface MissStats {
  hit_id: string; hits_7d: number; hits_30d: number; unique_visitors: number;
  sources: Partial<Record<"internal" | "external" | "search" | "direct", number>>;
  top_referrers: { name: string; n: number }[]; devices: { name: string; n: number }[];
  countries: { name: string; n: number }[]; redirect_hits: number | null;
}
export async function listMissStats(): Promise<Record<string, MissStats>> {
  const { data, error } = await db.rpc("get_miss_stats");
  if (error) throw error;
  return Object.fromEntries(((data ?? []) as MissStats[]).map((s) => [s.hit_id, s]));
}

/** Real public pages a missed address could be sent to. */
export async function listKnownPaths(): Promise<string[]> {
  const { data } = await db.from("blog_articles").select("slug, docs_section").eq("status", "published");
  const content = ((data ?? []) as { slug: string; docs_section: string | null }[])
    .map((a) => `/${a.docs_section ? "knowledgebase" : "blog"}/${a.slug}`);
  return ["/", "/about", "/pricing", "/blog", "/knowledgebase", "/studios", "/podcasts", "/partners", "/help", "/careers", "/auth?tab=signup", ...content];
}

export interface ScanResult {
  id: string; scanned_at: string; kind: "internal" | "outbound" | "image"; url: string; found_on: string;
  status_code: number | null; error: string | null; suggestion: string | null;
  status: "open" | "fixed" | "ignored"; fixed_at: string | null; fix_action: string | null; source_table: string | null;
}
export interface ScanImpact { id: string; views_before: number; views_after: number | null; misses_before: number; misses_after: number | null; redirect_hits: number | null }
export async function listScanImpact(): Promise<Record<string, ScanImpact>> {
  const { data, error } = await db.rpc("get_scan_impact");
  if (error) throw error;
  return Object.fromEntries(((data ?? []) as ScanImpact[]).map((r) => [r.id, r]));
}
export type FixAction = "redirect" | "swap" | "remove" | "ignore";
export async function applyLinkFixes(fixes: { id: string; action: FixAction; to?: string }[]): Promise<{ fixed: number; failed: { id: string; error: string }[] }> {
  const { data, error } = await db.functions.invoke("link-fix", { body: { fixes } });
  if (error) throw error;
  return data;
}
export async function setScanStatus(id: string, status: "open" | "ignored") {
  const { error } = await db.from("link_scan_results").update({ status }).eq("id", id);
  if (error) throw error;
}
export async function listScanResults(): Promise<ScanResult[]> {
  const { data, error } = await db.from("link_scan_results").select("*").order("status").order("kind").limit(1000);
  if (error) throw error;
  return data ?? [];
}
export async function runLinkScan(): Promise<{ checked: number; broken: number }> {
  const { data, error } = await db.functions.invoke("link-scan", { body: {} });
  if (error) throw error;
  return data;
}
